/**
 * HOP DONG MA LOI — docs/04-dac-ta-api.md §4.1 "Ma loi hop dong".
 *
 * Moi phan hoi loi cua API deu co dang:
 *   { "error": { "code": "...", "message": "...", "details": {} } }
 *
 * KHONG them/bot/doi ten ma loi ma khong cap nhat docs/04 truoc.
 */

export const ERROR_CODES = {
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  TWOFA_REQUIRED: 'TWOFA_REQUIRED',
  FORBIDDEN: 'FORBIDDEN',
  DOMAIN_FORBIDDEN: 'DOMAIN_FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NO_CONNECTION_AVAILABLE: 'NO_CONNECTION_AVAILABLE',
  CONNECTION_IN_USE: 'CONNECTION_IN_USE',
  ROLE_IN_USE: 'ROLE_IN_USE',
  OWNER_IMMUTABLE: 'OWNER_IMMUTABLE',
  LAST_ADMIN: 'LAST_ADMIN',
  RATE_LIMITED: 'RATE_LIMITED',
  CLOUDFLARE_ERROR: 'CLOUDFLARE_ERROR',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export interface ErrorCodeDef {
  readonly code: ErrorCode;
  readonly status: number;
  /** Thong diep mac dinh (tieng Viet) — service co the ghi de. */
  readonly message: string;
  /** Khi nao dung — trich docs/04 §4.1. */
  readonly when: string;
}

export const ERROR_CATALOG: Readonly<Record<ErrorCode, ErrorCodeDef>> = {
  UNAUTHENTICATED: {
    code: 'UNAUTHENTICATED',
    status: 401,
    message: 'Bạn chưa đăng nhập hoặc phiên đã hết hạn.',
    when: 'Chưa đăng nhập / token hết hạn',
  },
  TWOFA_REQUIRED: {
    code: 'TWOFA_REQUIRED',
    status: 401,
    message: 'Cần xác thực hai bước để tiếp tục.',
    when: 'Đăng nhập đúng mật khẩu, còn bước 2FA',
  },
  FORBIDDEN: {
    code: 'FORBIDDEN',
    status: 403,
    message: 'Bạn không có quyền thực hiện thao tác này.',
    when: 'Thiếu capability hệ thống',
  },
  DOMAIN_FORBIDDEN: {
    code: 'DOMAIN_FORBIDDEN',
    status: 403,
    message: 'Bạn không có quyền trên tên miền này.',
    when: 'Thiếu capability trên tên miền',
  },
  NOT_FOUND: {
    code: 'NOT_FOUND',
    status: 404,
    message: 'Không tìm thấy tài nguyên.',
    when: 'Không tồn tại hoặc ngoài phạm vi (không tiết lộ tồn tại)',
  },
  VALIDATION_ERROR: {
    code: 'VALIDATION_ERROR',
    status: 422,
    message: 'Dữ liệu không hợp lệ.',
    when: 'Dữ liệu sai, details.fields[]',
  },
  NO_CONNECTION_AVAILABLE: {
    code: 'NO_CONNECTION_AVAILABLE',
    status: 409,
    message: 'Không có kết nối Cloudflare khả dụng để thêm tên miền.',
    when: 'BR-5 không thoả',
  },
  CONNECTION_IN_USE: {
    code: 'CONNECTION_IN_USE',
    status: 409,
    message: 'Kết nối đang được sử dụng nên không thể xoá.',
    when: 'Xoá kết nối còn tên miền / đang mặc định',
  },
  ROLE_IN_USE: {
    code: 'ROLE_IN_USE',
    status: 409,
    message: 'Vai trò đang có người dùng, hãy chọn vai trò chuyển tiếp.',
    when: 'Xoá vai trò còn người dùng, thiếu fallbackRoleId',
  },
  OWNER_IMMUTABLE: {
    code: 'OWNER_IMMUTABLE',
    status: 409,
    message: 'Không thể thay đổi quyền của chủ sở hữu tên miền.',
    when: 'Thu hồi quyền chủ sở hữu',
  },
  LAST_ADMIN: {
    code: 'LAST_ADMIN',
    status: 409,
    message: 'Hệ thống phải luôn còn ít nhất một quản trị viên đang hoạt động.',
    when: 'Thao tác làm hệ thống hết admin',
  },
  RATE_LIMITED: {
    code: 'RATE_LIMITED',
    status: 429,
    message: 'Bạn thao tác quá nhanh, vui lòng thử lại sau.',
    when: 'Vượt giới hạn tốc độ',
  },
  CLOUDFLARE_ERROR: {
    code: 'CLOUDFLARE_ERROR',
    status: 502,
    message: 'Cloudflare trả về lỗi.',
    when: 'Cloudflare trả lỗi, details.cfErrors[]',
  },
};

export const ERROR_CODE_IDS: readonly ErrorCode[] = Object.keys(ERROR_CATALOG) as ErrorCode[];

export function httpStatusForErrorCode(code: ErrorCode): number {
  return ERROR_CATALOG[code].status;
}

/* ── Hinh dang phan hoi (docs/04 §4.1) ────────────────────────────── */

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    details?: Record<string, unknown>;
  };
}

export interface ApiMeta {
  page: number;
  perPage: number;
  total: number;
}

export interface ApiSuccessBody<T> {
  data: T;
  meta?: ApiMeta;
}

/** Chi tiet kem theo VALIDATION_ERROR — docs/04 §4.1 `details.fields[]`. */
export interface FieldError {
  field: string;
  message: string;
}
